import { GlAnimatedChevronLgDownUpIcon, GlButton, GlLink, GlLoadingIcon, GlTooltipDirective } from '@gitlab/ui';
import { markRaw } from 'vue';
import { normalizeHeaders } from '@gitlab/frontend-utils';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { logError } from '~/lib/logger';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { __ } from '~/locale';
import Poll from '~/lib/utils/poll';
import HelpPopover from '~/vue_shared/components/help_popover.vue';
import { DynamicScroller, DynamicScrollerItem } from 'vendor/vue-virtual-scroller';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
import { EXTENSION_ICONS } from '../../constants';
import { generateText } from './utils';
import { createTelemetryHub } from './telemetry';
import ContentRow from './widget_content_row.vue';
import DynamicContent from './dynamic_content.vue';
import StatusIcon from './status_icon.vue';
import ActionButtons from './action_buttons.vue';
const WIDGET_PREFIX = 'Widget';
const MISSING_RESPONSE_HEADERS = 'MR Widget: response object should contain status and headers object. Make sure to include that in your `fetchCollapsedData` and `fetchExpandedData` functions.';
const LOADING_STATE_COLLAPSED = 'collapsed';
const LOADING_STATE_EXPANDED = 'expanded';
const LOADING_STATE_STATUS_ICON = 'status_icon';
export default {
    name: 'MrWidget',
    MISSING_RESPONSE_HEADERS,
    LOADING_STATE_COLLAPSED,
    LOADING_STATE_EXPANDED,
    LOADING_STATE_STATUS_ICON,
    components: {
        ActionButtons,
        StatusIcon,
        GlLink,
        GlButton,
        GlLoadingIcon,
        GlAnimatedChevronLgDownUpIcon,
        ContentRow,
        DynamicContent,
        DynamicScroller,
        DynamicScrollerItem,
        HelpPopover
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        SafeHtml
    },
    mixins: [
        glSlotsMixin
    ],
    props: {
        loadingText: {
            type: String,
            required: false,
            default: __('Loading')
        },
        // Use this property when you need to control the loading state from the
        // parent component.
        loadingState: {
            type: String,
            required: false,
            default: undefined,
            validator: (s)=>{
                if (!s) {
                    return true;
                }
                return [
                    LOADING_STATE_EXPANDED,
                    LOADING_STATE_COLLAPSED,
                    LOADING_STATE_STATUS_ICON
                ].includes(s);
            }
        },
        errorText: {
            type: String,
            required: false,
            default: __('Failed to load')
        },
        fetchCollapsedData: {
            type: Function,
            required: false,
            default: undefined
        },
        fetchExpandedData: {
            type: Function,
            required: false,
            default: undefined
        },
        // If the summary slot is not used, this value will be used as a fallback.
        summary: {
            type: Object,
            required: false,
            default: undefined,
            validator: (s)=>{
                return Boolean(s.title);
            }
        },
        // If the content slot is not used, this value will be used as a fallback.
        content: {
            type: Array,
            required: false,
            default: undefined
        },
        multiPolling: {
            type: Boolean,
            required: false,
            default: false
        },
        statusIconName: {
            type: String,
            required: false,
            default: 'neutral',
            validator: (value)=>Object.keys(EXTENSION_ICONS).indexOf(value) > -1
        },
        /**
     * When isCollapsible is true, expandButtonLabel and collapseButtonLabel should also be passed.
     */ isCollapsible: {
            type: Boolean,
            required: true
        },
        expandButtonLabel: {
            type: String,
            required: false,
            default: __('Show details')
        },
        collapseButtonLabel: {
            type: String,
            required: false,
            default: __('Hide details')
        },
        /**
     * A button is composed of the following properties:
     *
     * {
     *   "id": string,
     *   "href": string,
     *   "dataMethod": string,
     *   "dataClipboardText": string,
     *   "icon": string,
     *   "variant": string,
     *   "loading": boolean,
     *   "testId":string,
     *   "text": string,
     *   "class": string | Object,
     *   "trackFullReportClicked": boolean,
     * }
     */ actionButtons: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        widgetName: {
            type: String,
            required: true,
            // see https://docs.gitlab.com/ee/development/fe_guide/merge_request_widgets.html#add-new-widgets
            validator: (val)=>val.startsWith(WIDGET_PREFIX)
        },
        telemetry: {
            type: Boolean,
            required: false,
            default: true
        },
        /**
     * @typedef {Object} helpPopover
     * @property {Object} options
     * @property {String} options.title
     * @property {Object} content
     * @property {String} content.text
     * @property {String} content.learnMorePath
     */ helpPopover: {
            type: Object,
            required: false,
            default: null
        },
        // When this is provided, the widget will display an error message in the summary section.
        hasError: {
            type: Boolean,
            required: false,
            default: false
        },
        level: {
            type: Number,
            required: false,
            default: 1,
            validator: (value)=>value === 1 || value === 2
        },
        persistContent: {
            type: Boolean,
            required: false,
            default: false
        },
        showContentWhenCollapsed: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'is-loading',
        'toggle'
    ],
    data () {
        return {
            isExpandedForTheFirstTime: true,
            isCollapsed: true,
            isLoadingCollapsedContent: true,
            isLoadingExpandedContent: false,
            summaryError: null,
            contentError: null,
            telemetryHub: null,
            activePolls: markRaw([])
        };
    },
    computed: {
        isSummaryLoading () {
            return this.isLoadingCollapsedContent || this.loadingState === LOADING_STATE_COLLAPSED;
        },
        shouldShowLoadingIcon () {
            return this.isSummaryLoading || this.loadingState === LOADING_STATE_STATUS_ICON;
        },
        generatedSummary () {
            return generateText(this.summary?.title || '');
        },
        generatedSubSummary () {
            return generateText(this.summary?.subtitle || '');
        },
        toggleButtonLabel () {
            return this.isCollapsed ? this.expandButtonLabel : this.collapseButtonLabel;
        },
        summaryStatusIcon () {
            return this.summaryError ? this.$options.failedStatusIcon : this.statusIconName;
        },
        hasActionButtons () {
            return this.actionButtons.length > 0 || Boolean(this.glSlots()['action-buttons']);
        },
        contentWithKeyField () {
            return this.content?.map((item, index)=>({
                    ...item,
                    id: item.id || index
                }));
        }
    },
    watch: {
        hasError: {
            handler (newValue) {
                this.summaryError = newValue ? this.errorText : null;
            },
            immediate: true
        },
        isLoadingCollapsedContent (newValue) {
            this.$emit('is-loading', newValue);
        },
        isCollapsible (newValue) {
            if (!newValue) this.isCollapsed = true;
        }
    },
    created () {
        if (this.telemetry) {
            this.telemetryHub = createTelemetryHub(this.widgetName);
        }
    },
    async mounted () {
        this.telemetryHub?.viewed();
        try {
            if (this.fetchCollapsedData) {
                this.isLoadingCollapsedContent = true;
                await this.fetch(this.fetchCollapsedData);
            }
        } catch  {
            this.summaryError = this.errorText;
        } finally{
            this.isLoadingCollapsedContent = false;
        }
    },
    beforeDestroy () {
        this.activePolls.forEach((poll)=>poll.stop());
    },
    methods: {
        onActionClick (action) {
            if (action.trackFullReportClicked) {
                this.telemetryHub?.fullReportClicked();
            }
        },
        toggleCollapsed () {
            this.isCollapsed = !this.isCollapsed;
            if (this.isExpandedForTheFirstTime) {
                this.telemetryHub?.expanded({
                    type: this.summaryStatusIcon
                });
                if (typeof this.fetchExpandedData === 'function') {
                    this.isExpandedForTheFirstTime = false;
                    this.fetchExpandedContent();
                }
            }
            this.$emit('toggle', {
                expanded: !this.isCollapsed
            });
        },
        async fetchExpandedContent () {
            this.isLoadingExpandedContent = true;
            this.contentError = null;
            try {
                await this.fetch(this.fetchExpandedData);
            } catch  {
                this.contentError = this.errorText;
                // Reset these values so that we allow refetching
                this.isExpandedForTheFirstTime = true;
                this.isCollapsed = true;
            }
            this.isLoadingExpandedContent = false;
        },
        fetch (handler) {
            const requests = this.multiPolling ? handler() : [
                handler
            ];
            const promises = requests.map((request)=>{
                return new Promise((resolve, reject)=>{
                    const poll = new Poll({
                        resource: {
                            fetchData: ()=>request()
                        },
                        method: 'fetchData',
                        successCallback: (response)=>{
                            if (typeof response.status === 'undefined' || typeof response.headers === 'undefined') {
                                logError(MISSING_RESPONSE_HEADERS);
                                throw new Error(MISSING_RESPONSE_HEADERS);
                            }
                            const headers = normalizeHeaders(response.headers);
                            if (headers['POLL-INTERVAL']) {
                                return;
                            }
                            resolve(response.data);
                        },
                        errorCallback: (e)=>{
                            Sentry.captureException(e);
                            reject(e);
                        }
                    });
                    poll.makeRequest();
                    this.activePolls.push(poll);
                });
            });
            return Promise.all(promises);
        }
    },
    failedStatusIcon: EXTENSION_ICONS.failed,
    i18n: {
        learnMore: __('Learn more')
    }
};
